import React, { useRef, useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import Scheduler from './Scheduler';
import { useSelector } from 'react-redux';
import { useGetCurrentUserQuery } from "../../../services/authApi";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import SimpleBar from 'simplebar-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'simplebar-react/dist/simplebar.min.css';
import thirtymins from './thirtyMins';
import sixtymins from './sixtyMins';
import api from '../../../services/api';

function Calendar() {
  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];
const to12Hour = (time24) =>
  moment(time24, "HH:mm").format("hh:mm A");

  const userDetails = useSelector(state => state.userReducer);
  const deviceTimezone = localStorage.getItem('device_timezone');
  const userTimezone = userDetails?.user?.data?.timezone?.name || deviceTimezone;

  const initialSelectedDate = moment.tz(new Date(), userTimezone).format('YYYY-MM-DD');
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);

  const [date, setDate] = useState([]);
  const [activeMonth, setActiveMonth] = useState(months[new Date().getMonth()]);
  const [coachSlotData, setCoachSlotData] = useState([]);
  const activeMonthSwiper = useRef();

  const { data: currentUser } = useGetCurrentUserQuery({});

  // Base time slots (do not mutate)
  const baseTimeArr = useMemo(() => {
    if (!currentUser?.data) return [];
    return currentUser.data.coach_time_slot === '30_min' ? thirtymins : sixtymins;
  }, [currentUser]);
// console.log(baseTimeArr);

  // Generate days for active month
  useEffect(() => {
    generateDays(months.indexOf(activeMonth));
  }, [activeMonth]);

  const generateDays = (monthIndex) => {
    const year = new Date().getFullYear();
    const daysInMonth = moment.tz({ year, month: monthIndex }, userTimezone).daysInMonth();
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDate(daysArray);
  };

  // Fetch booked sessions for selectedDate and previous day
useEffect(() => {
  if (!selectedDate) return;

  const prevDayUTC = moment.tz(selectedDate, userTimezone).subtract(1, 'day').format('YYYY-MM-DD');

  // Fetch both selectedDate and previous day
  Promise.all([
    api.get(`employee/get-timeslots/?date=${selectedDate}`
    //   , {
    //   headers: { authorization: `Bearer ${localStorage.getItem("livtoken")}` }
    // }
  ),
    api.get(`employee/get-timeslots/?date=${prevDayUTC}`
    //   , {
    //   headers: { authorization: `Bearer ${localStorage.getItem("livtoken")}` }
    // }
  )
  ])
    .then(([resToday, resPrev]) => {
      const allBooked = [
        ...(resToday.data.data.booked_slots || []),
        ...(resPrev.data.data.booked_slots || [])
      ];

      // Convert all sessions to local timezone
      const localSessions = allBooked.map(slot => {
        const localDateTime = moment.utc(`${slot.session_date}T${slot.session_time}:00Z`).tz(userTimezone);
        return {
          ...slot,
          localDate: localDateTime.format('YYYY-MM-DD'),
          localTime: localDateTime.format('HH:mm')
        };
      });

      // Only keep sessions that fall on selectedDate in local timezone
      const sessionsForDay = localSessions.filter(s => s.localDate === selectedDate);

      setCoachSlotData(sessionsForDay);
    })
    .catch(() => setCoachSlotData([]));
}, [selectedDate, userTimezone]);

  // Map sessions to scheduler
  const mappedTimeArr = useMemo(() => {
    if (!baseTimeArr?.length) return [];

    const tempArr = baseTimeArr.map(slot => ({
      ...slot,
      event: null,
      clientId: null,
      localizedTime: slot.time
    }));

    coachSlotData.forEach(slot => {
      const slotIndex = tempArr.findIndex(t => t.time === slot.localTime);
      if (slotIndex !== -1) {
        tempArr[slotIndex].event = slot.user_name;
        tempArr[slotIndex].clientId = slot.user_id;
        tempArr[slotIndex].localizedTime = slot.localTime;
      } else {
        // Add dynamically if time not in base slots
        tempArr.push({
          time: slot.localTime,
          event: slot.user_name,
          clientId: slot.user_id,
          localizedTime: slot.localTime
        });
      }
    });

    // Sort safely
    return tempArr
      .filter(t => t.localizedTime)
      .sort((a, b) => a.localizedTime.localeCompare(b.localizedTime));
  }, [baseTimeArr, coachSlotData]);
// console.log(mappedTimeArr);

  return (
    <aside className="bg-white rounded-2xl w-[650px] 2xl:w-[900px] p-4 h-[92%] mt-8 mr-20 overflow-hidden">
      <div className="xs:w-full p-4 rounded-[30px]">
        <p className="font-bold text-xl font-constantia">
          Working hours <span className="text-sm font-ubuntu font-normal pl-10 text-gray-400">
            {moment(selectedDate).format('dddd')}
          </span>
        </p>

        {/* Calendar */}
        <div className="border-2 rounded-2xl border-solid border-orange-200 w-full mt-4">
          <Swiper
            modules={[Navigation]}
            spaceBetween={40}
            slidesPerView={4}
            className="workingHours w-full px-10"
            navigation
            initialSlide={new Date().getMonth()}
          >
            {months.map((month, key) => (
              <SwiperSlide
                key={key}
                ref={activeMonthSwiper}
                 className={`text-center w-auto px-4 ${
        activeMonth === month ? "text-black-900 font-bold" : "text-gray-400"
      } font-ubuntu cursor-pointer`}
                // className={`text-center !w-max ${activeMonth === month ? "text-black-900" : "text-gray-400"} font-ubuntu cursor-pointer`}
                onClick={() => {
                  setActiveMonth(month);
                  generateDays(key);
                }}
              >
                {month}
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="w-full flex mt-4 ml-5">
            <ul className="flex flex-wrap w-[500px] overflow-hidden mt-[-3px]">
              {date.map((day, key) => {
                const year = new Date().getFullYear();
                const monthIndex = months.indexOf(activeMonth);
                const localDay = moment.tz({ year, month: monthIndex, day }, userTimezone).format('YYYY-MM-DD');

                return (
                  <li
                    key={key}
                    className={`px-[0.555rem] pt-[5px] pb-[5px] cursor-pointer 
                      ${localDay === selectedDate ? "rounded-full bg-[#FFC289]" : ""}`}
                    onClick={() => setSelectedDate(localDay)}
                  >
                    {day}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        {/* Scheduler */}
        <SimpleBar className='lg:h-[25vh] xl:h-[58vh] 2xl:h-[70vh]' forceVisible="y" autoHide={false}>
          <div className='mt-6'>
            {mappedTimeArr.map((item, index) => (
              <Scheduler
                key={index}
                event={item.event}
                // item={item.localizedTime || item.time}
                 item={to12Hour(item.localizedTime || item.time)}
                clientId={item.clientId}
                sessionTime={currentUser?.data?.coach_time_slot}
              />
            ))}
          </div>
        </SimpleBar>
      </div>
    </aside>
  );
}

export default Calendar;



